import { useState, useMemo, useEffect } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useQueryParam, useQueryParams } from '~/hooks/nav/useQueryParam';
import {
  visibilityParamToState,
  visibilityStateToParam,
  columnFiltersParamToState,
  columnFiltersStateToParam,
  sortingParamToState,
  sortingStateToParam,
  DEFAULT_TABLE_PAGE_SIZE,
  parseTablePageParam,
  parseTablePageSizeParam,
} from '~/logic/table-utils';
import { atomWithStorage } from 'jotai/utils';
import { useAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { createUrl } from '~/logic/url/create-url';
import { useRouter } from 'next/router';

// two types of table state:
// 1. query param table state: stored in query params
// 2. local table state: stored in local storage
// we always take priority on query param table state
type TableState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  columnVisibility: VisibilityState;
  sorting: SortingState;
  pagination: PaginationState;
};

type UseTableFiltersAndSortingProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  enableQueryParam?: boolean;
  storageKey?: string;
  queryParamPrefix?: string;
};

function getColumnSortId<TData, TValue>(column: ColumnDef<TData, TValue>): string | undefined {
  if (column.id) {
    return column.id;
  }
  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return column.accessorKey;
  }
  return undefined;
}

const createTableStateAtom = (storageKey: string) =>
  atomWithStorage<TableState>(
    storageKey,
    {
      globalFilter: '',
      columnFilters: [],
      columnVisibility: {},
      sorting: [],
      pagination: { pageIndex: 0, pageSize: DEFAULT_TABLE_PAGE_SIZE },
    },
    undefined,
    { getOnInit: true },
  ); // getOnInit is needed to get the value on first render

export function useTableFiltersAndSorting<TData, TValue>({
  columns,
  enableQueryParam = false,
  storageKey,
  queryParamPrefix = '',
}: UseTableFiltersAndSortingProps<TData, TValue>) {
  const router = useRouter();
  const tableStateAtom = useMemo(() => createTableStateAtom(storageKey ?? 'dummy_table_key'), [storageKey]);
  const [localTableState, setLocalTableState] = useAtom(tableStateAtom);

  const searchParams = useSearchParams();
  const enableStorage = storageKey !== undefined;

  // Helper to prefix query param names
  const prefixParam = (param: string) => (queryParamPrefix ? `${queryParamPrefix}${param}` : param);

  // Global filtering
  const [searchQueryParam, setSearchQueryParam] = useQueryParam(prefixParam('search'));
  const [globalFilter, setGlobalFilter] = useState<string>(
    enableQueryParam && searchQueryParam ? searchQueryParam
    : enableStorage ? localTableState.globalFilter
    : '',
  );

  // Sync search filter when URL param changes
  useEffect(() => {
    if (enableQueryParam && searchQueryParam !== undefined && searchQueryParam !== globalFilter) {
      setGlobalFilter(searchQueryParam);
    }
  }, [enableQueryParam, searchQueryParam, globalFilter]);

  // Column filtering
  // e.g. ["createdBy", "testSuites", "tags"]
  const filterableColumns = useMemo(
    () => columns.filter((column) => column.enableColumnFilter).map((column) => column.id || ''),
    [columns],
  );
  const prefixedFilterableColumns = useMemo(
    () => filterableColumns.map((col) => prefixParam(col)),
    [filterableColumns, queryParamPrefix],
  );
  const [filterParams, setFilterParams] = useQueryParams(prefixedFilterableColumns);
  // Strip prefix from keys when converting to state
  const unprefixedFilterParams = useMemo(() => {
    if (!queryParamPrefix) {
      return filterParams;
    }
    return Object.fromEntries(
      Object.entries(filterParams).map(([key, value]) => [
        key.startsWith(queryParamPrefix) ? key.slice(queryParamPrefix.length) : key,
        value,
      ]),
    );
  }, [filterParams, queryParamPrefix]);
  const columnFiltersState = columnFiltersParamToState(unprefixedFilterParams);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    enableQueryParam && columnFiltersState.length > 0 ? columnFiltersState
    : enableStorage ? localTableState.columnFilters
    : [],
  );

  // Column visibility
  const [visibilityParams, setVisibilityParams] = useQueryParam(prefixParam('hidden'));
  const visibilityState = visibilityParamToState(visibilityParams);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    enableQueryParam && Object.keys(visibilityState).length > 0 ? visibilityState
    : enableStorage ? localTableState.columnVisibility
    : {},
  );

  // Sorting
  const sortByParam = prefixParam('sortBy');
  const sortDirectionParam = prefixParam('sortDirection');
  const [sortingParams, setSortingParams] = useQueryParams([sortByParam, sortDirectionParam]);
  const sortableColumnIds = useMemo(
    () =>
      new Set(
        columns
          .filter((col) => col.enableSorting !== false)
          .map((col) => getColumnSortId(col))
          .filter((id): id is string => Boolean(id)),
      ),
    [columns],
  );
  const sortingState = sortingParamToState(sortingParams[sortByParam], sortingParams[sortDirectionParam]).filter((s) =>
    sortableColumnIds.has(s.id),
  );
  const storedSorting = enableStorage ? localTableState.sorting.filter((s) => sortableColumnIds.has(s.id)) : [];
  const [sorting, setSorting] = useState<SortingState>(
    enableQueryParam && sortingState.length > 0 ? sortingState
    : enableStorage ? storedSorting
    : [],
  );

  // Clean up stale sorting URL params that reference non-sortable columns
  useEffect(() => {
    if (!enableQueryParam) {
      return;
    }
    const rawSortBy = sortingParams[sortByParam];
    if (rawSortBy && !sortableColumnIds.has(rawSortBy)) {
      setSortingParams({ [sortByParam]: '', [sortDirectionParam]: '' });
    }
  }, [enableQueryParam, sortingParams, sortByParam, sortDirectionParam, sortableColumnIds, setSortingParams]);

  // Pagination - only persisted to URL, not local storage (pagination is ephemeral)
  const pageParam = prefixParam('page');
  const pageSizeParam = prefixParam('pageSize');
  const [paginationParams, setPaginationParams] = useQueryParams([pageParam, pageSizeParam]);
  const pageFromUrl = parseTablePageParam(paginationParams[pageParam]);
  const pageSizeFromUrl = parseTablePageSizeParam(paginationParams[pageSizeParam]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: enableQueryParam ? pageFromUrl - 1 : 0,
    pageSize: enableQueryParam ? pageSizeFromUrl : DEFAULT_TABLE_PAGE_SIZE,
  });

  // Sync pagination state when URL params change (handles direct navigation and browser back/forward)
  useEffect(() => {
    if (!enableQueryParam) {
      return;
    }
    const newPageIndex = pageFromUrl - 1;
    const newPageSize = pageSizeFromUrl;
    setPagination((prev) => {
      if (prev.pageIndex === newPageIndex && prev.pageSize === newPageSize) {
        return prev;
      }
      return { pageIndex: newPageIndex, pageSize: newPageSize };
    });
  }, [enableQueryParam, pageFromUrl, pageSizeFromUrl]);

  // Normalize URL pagination params so invalid values don't desync UI state and API requests
  useEffect(() => {
    if (!enableQueryParam) {
      return;
    }
    const canonicalPage = pageFromUrl > 1 ? String(pageFromUrl) : '';
    const canonicalPageSize = pageSizeFromUrl !== DEFAULT_TABLE_PAGE_SIZE ? String(pageSizeFromUrl) : '';
    const currentPage = paginationParams[pageParam] ?? '';
    const currentPageSize = paginationParams[pageSizeParam] ?? '';

    if (currentPage === canonicalPage && currentPageSize === canonicalPageSize) {
      return;
    }

    setPaginationParams({
      [pageParam]: canonicalPage,
      [pageSizeParam]: canonicalPageSize,
    });
  }, [enableQueryParam, pageFromUrl, pageSizeFromUrl, pageParam, pageSizeParam, paginationParams, setPaginationParams]);

  // Update query params with localTableState on page load if there are no query params
  useEffect(() => {
    // Don't update table query params if any drawer is open (drawer query params contain "drawer" in the key)
    const hasDrawerParam = Object.keys(router.query).some((key) => key.toLowerCase().includes('drawer'));
    if (hasDrawerParam) {
      return;
    }

    if (enableQueryParam && enableStorage) {
      if (searchParams.size === 0) {
        // Restore table state from local storage to URL
        // Use router.replace with shallow routing so Next.js router.query updates
        // (window.history.replaceState would update the URL bar but leave router.query stale,
        //  causing server-side filters like search to not be applied)
        const columnFiltersWithPrefix = Object.fromEntries(
          Object.entries(columnFiltersStateToParam(localTableState.columnFilters, filterableColumns)).map(
            ([key, value]) => [prefixParam(key), value],
          ),
        );
        const fullUrl = createUrl(window.location.origin + window.location.pathname, {
          [prefixParam('search')]: localTableState.globalFilter,
          [prefixParam('hidden')]: visibilityStateToParam(localTableState.columnVisibility),
          [sortByParam]: sortingStateToParam(storedSorting).sortBy,
          [sortDirectionParam]: sortingStateToParam(storedSorting).sortDirection,
          // Note: pagination is not restored from local storage (ephemeral state)
          ...columnFiltersWithPrefix,
        });
        // Extract relative URL (pathname + search) for router.replace
        const url = new URL(fullUrl);
        void router.replace(url.pathname + url.search, undefined, { shallow: true });
      }
    }
  }, [searchParams.size]);

  // Handlers
  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
    if (enableQueryParam) {
      setSearchQueryParam(value);
    }
    if (enableStorage) {
      void setLocalTableState((prev: TableState) => ({ ...prev, globalFilter: value }));
    }
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) => {
    const newColumnFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    setColumnFilters(newColumnFilters);

    if (enableQueryParam) {
      // Add prefix to column filter keys when setting params
      const paramsWithPrefix = Object.fromEntries(
        Object.entries(columnFiltersStateToParam(newColumnFilters, filterableColumns)).map(([key, value]) => [
          prefixParam(key),
          value,
        ]),
      );
      setFilterParams(paramsWithPrefix);
    }
    if (enableStorage) {
      void setLocalTableState((prev: TableState) => ({ ...prev, columnFilters: newColumnFilters }));
    }
  };

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const newColumnVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newColumnVisibility);

    if (enableQueryParam) {
      setVisibilityParams(visibilityStateToParam(newColumnVisibility));
    }
    if (enableStorage) {
      void setLocalTableState((prev: TableState) => ({ ...prev, columnVisibility: newColumnVisibility }));
    }
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);

    if (enableQueryParam) {
      const { sortBy, sortDirection } = sortingStateToParam(newSorting);
      setSortingParams({ [sortByParam]: sortBy, [sortDirectionParam]: sortDirection });
    }
    if (enableStorage) {
      void setLocalTableState((prev: TableState) => ({ ...prev, sorting: newSorting }));
    }
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const nextPagination = typeof updater === 'function' ? updater(pagination) : updater;
    const newPagination = {
      pageIndex: Math.max(0, Math.floor(nextPagination.pageIndex)),
      pageSize: parseTablePageSizeParam(nextPagination.pageSize),
    };
    setPagination(newPagination);

    if (enableQueryParam) {
      setPaginationParams({
        [pageParam]: newPagination.pageIndex > 0 ? String(newPagination.pageIndex + 1) : '',
        [pageSizeParam]: newPagination.pageSize !== DEFAULT_TABLE_PAGE_SIZE ? String(newPagination.pageSize) : '',
      });
    }
    // Note: pagination is not persisted to local storage (ephemeral state, URL-only)
  };

  return {
    // States
    globalFilter,
    columnFilters,
    columnVisibility,
    sorting,
    pagination,

    // Handlers
    handleGlobalFilterChange,
    handleColumnFiltersChange,
    handleColumnVisibilityChange,
    handleSortingChange,
    handlePaginationChange,

    // For table state
    filterState: {
      globalFilter,
      columnFilters,
      columnVisibility,
      sorting,
      pagination,
    },
  };
}
